import { QueryClient, QueryKey } from "@tanstack/react-query";

async function optimisticallyUpdateQueryData<TDto>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  update: (cachedData: TDto) => Partial<TDto>,
) {
  await queryClient.cancelQueries(queryKey);
  const cachedData = queryClient.getQueryData<TDto>(queryKey);

  if (cachedData) {
    const updatedData: TDto = {
      ...cachedData,
      ...update(cachedData),
    };

    queryClient.setQueryData(queryKey, updatedData);
  }

  return cachedData;
}

async function optimisticallyUpdateQueriesData<TDto>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  update: (cachedData: TDto) => Partial<TDto>,
  filter?: (data: TDto) => boolean,
) {
  await queryClient.cancelQueries(queryKey);

  const cachedData = new Map<QueryKey, TDto>();
  const queries = queryClient.getQueriesData<TDto>(queryKey);

  queries.forEach(([key, data]) => {
    if (data && (!filter || filter(data))) {
      cachedData.set(key, data);
      queryClient.setQueryData(key, {
        ...data,
        ...update(data),
      });
    }
  });

  return cachedData;
}

interface QueryParams {
  filters?: {
    [filterProperty: string]: string[] | null;
  };
  sortProperty?: string;
  sortDirection?: "asc" | "desc";
}

const UrlUtilities = {
  appendQueryParams: (url: URL, queryParams?: QueryParams) => {
    for (const filterProperty in queryParams?.filters) {
      const filterValues = queryParams.filters[filterProperty];

      if (filterValues != null && filterValues.length > 0) {
        url.searchParams.append(filterProperty, filterValues.join(","));
      }
    }

    if (queryParams?.sortProperty !== undefined && queryParams?.sortDirection !== undefined) {
      url.searchParams.append("sortBy", `${queryParams.sortDirection}(${queryParams.sortProperty})`);
    }
  },
};

export { optimisticallyUpdateQueriesData, optimisticallyUpdateQueryData, UrlUtilities };
export type { QueryParams };
